import React, { createContext, useContext, useMemo } from 'react';

/**
 * How a recorded clip becomes text, and what to do when the microphone is
 * refused. Each app mounts one (with its API call and its prerelease check);
 * a `TextInput` under no provider, or under `NullDictationProvider`, draws no
 * mic at all.
 */
export interface Dictation {
  /** The clip's words. `field` is the stable code of the field it is for. */
  transcribe(blob: Blob, field: string, hint?: string): Promise<string>;
  /** The browser refused the microphone (`NotAllowedError`); not called when there is none. */
  onPermissionDenied?(): void;
}

const DictationContext = createContext<Dictation | null>(null);

export interface DictationProviderProps extends Dictation {
  /**
   * False provides no dictation (the feature is hidden from this viewer).
   * A prop rather than an unmounted provider, so the gate answering never
   * remounts the app beneath it.
   */
  enabled?: boolean;
  children: React.ReactNode;
}

export function DictationProvider({
  transcribe,
  onPermissionDenied,
  enabled = true,
  children,
}: DictationProviderProps) {
  const value = useMemo<Dictation | null>(
    () => (enabled ? { transcribe, onPermissionDenied } : null),
    [enabled, transcribe, onPermissionDenied],
  );
  return <DictationContext.Provider value={value}>{children}</DictationContext.Provider>;
}

/** No dictation below here: the tour's demo panels never reach the endpoint. */
export function NullDictationProvider({ children }: { children: React.ReactNode }) {
  return <DictationContext.Provider value={null}>{children}</DictationContext.Provider>;
}

/** The nearest provider's dictation, or null when there is none. */
export function useDictationContext(): Dictation | null {
  return useContext(DictationContext);
}
